import { useRef, useState, type DragEvent } from "react";

interface Props {
  onFile: (file: File) => void;
  busy: boolean;
  fileName: string | null;
  /** What the empty zone says. */
  prompt?: string;
  /** The zone's accessible name before a file is chosen. */
  label?: string;
}

/** Where the PDF is dropped, or picked. One file at a time: each quotation is
 * read, checked and saved on its own, so a batch is dropped one by one. */
export default function DropZone({ onFile, busy, fileName, prompt = "Drop a supplier's quotation PDF here", label = "Choose a supplier's quotation PDF" }: Props) {
  const [over, setOver] = useState(false);
  const input = useRef<HTMLInputElement>(null);

  function choose() {
    if (!busy) {
      input.current?.click();
    }
  }

  function take(files: FileList | null) {
    const file = files?.[0];
    if (file !== undefined) {
      onFile(file);
    }
  }

  function onDrop(event: DragEvent<HTMLDivElement>) {
    event.preventDefault();
    setOver(false);
    if (!busy) {
      take(event.dataTransfer.files);
    }
  }

  return (
    <div
      className={`qr-drop${over ? " over" : ""}${busy ? " busy" : ""}${fileName ? " loaded" : ""}`}
      onDragOver={(event) => {
        event.preventDefault();
        setOver(true);
      }}
      onDragLeave={() => setOver(false)}
      onDrop={onDrop}
      onClick={choose}
      role="button"
      tabIndex={0}
      aria-label={fileName ? `Replace ${fileName}` : label}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          choose();
        }
      }}
    >
      <input
        ref={input}
        type="file"
        accept="application/pdf,.pdf"
        hidden
        disabled={busy}
        data-testid="reader-file"
        onChange={(event) => {
          take(event.target.files);
          event.target.value = "";
        }}
      />
      {busy ? (
        <strong>Reading…</strong>
      ) : (
        <>
          <strong>{fileName ?? prompt}</strong>
          <span>{fileName ? "Drop another to replace it" : "or click to choose one · one file at a time"}</span>
        </>
      )}
    </div>
  );
}
